import { useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from '@sales-center/components'
import { IMoreMenuItem } from '@sales-center/components'
import { permissionDenied } from '@user/store/authorization/permissions'
import { useAppDispatch } from '@user/store/hooks/useAppDispatch'
import {
  getLeadDetail,
  getLeadList,
  updateSaleTeamOwner,
} from '@user/store/lead'
import { getLeadNotes } from '@user/store/leadNotes/api'
import { useDialogState } from '@user/utils/useDialogState'
import { LeadParentHeaderProps } from '../typings'
import { ReassignDialogState } from './typings'

export function useReassignDialog(
  params: LeadParentHeaderProps,
): ReassignDialogState {
  const {
    canUpdate,
    detailPage,
    lead: { id },
  } = params

  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  const [open, onOpen, onClose] = useDialogState()

  const reassignMenuItem = useMemo<IMoreMenuItem>(
    () => ({
      label: 'Reassign',
      onClick: onOpen,
      disabled: !canUpdate,
      dataTestId: `reassignBtn-${id}`,
      tooltipText: canUpdate ? undefined : permissionDenied,
    }),
    [canUpdate, onOpen],
  )

  const onUpdate = useCallback(
    async (teamId: number, ownerId: string, removeServices: boolean) => {
      const result = await dispatch(
        updateSaleTeamOwner({
          id: id,
          salesTeamId: teamId,
          assigneeId: ownerId,
          removeServices: removeServices,
        }),
      )

      const updateDetail = await dispatch(getLeadDetail(id))
      const updateNotes = await dispatch(getLeadNotes({ leadId: id }))

      if (
        updateSaleTeamOwner.fulfilled.match(result) &&
        getLeadDetail.fulfilled.match(updateDetail) &&
        getLeadNotes.fulfilled.match(updateNotes)
      ) {
        toast.success('Lead Sales Team updated')
        // Display the optional toast message if any Draft agreements were updated
        if (result.payload.draftAgreementsUpdated) {
          toast.success('Information has been updated on any draft agreements.')
        }
        onClose()
        dispatch(getLeadList())
      } else if (getLeadDetail.rejected.match(updateDetail)) {
        const { payload } = updateDetail
        if (!!payload) {
          if (payload.StatusCode === 403) {
            dispatch(getLeadList())
            if (detailPage) navigate('/leads')
          }
          if (
            payload.StatusCode === 500 &&
            payload.Message?.toLowerCase() === 'permission denied'
          ) {
            navigate('/access-denied')
          }
        }
      } else {
        toast.error(`Unable to update Lead Sales Team.`)
      }
    },
    [detailPage, dispatch, navigate, id, onClose],
  )

  return {
    open,
    onOpen,
    onClose,
    reassignMenuItem,
    onUpdate,
  }
}
